import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, UserPlus, Activity, UserX, MessageCircle, Send, CalendarClock, TrendingUp,
} from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { api } from "@/lib/api";
import { fmtDate, statusColor } from "@/lib/format";
import { Button } from "@/components/ui/button";

const KPIS = [
  { key: "total", label: "Total de Clientes", icon: Users, color: "text-emerald-600", bg: "bg-emerald-50" },
  { key: "novos", label: "Clientes Novos (30d)", icon: UserPlus, color: "text-sky-600", bg: "bg-sky-50" },
  { key: "ativos", label: "Clientes Ativos", icon: Activity, color: "text-emerald-600", bg: "bg-emerald-50" },
  { key: "inativos", label: "Clientes Inativos", icon: UserX, color: "text-slate-500", bg: "bg-slate-100" },
  { key: "com_whatsapp", label: "Com WhatsApp", icon: MessageCircle, color: "text-emerald-600", bg: "bg-emerald-50" },
  { key: "total_mensagens", label: "Mensagens Enviadas", icon: Send, color: "text-indigo-600", bg: "bg-indigo-50" },
  { key: "mensagens_hoje", label: "Enviadas Hoje", icon: TrendingUp, color: "text-amber-600", bg: "bg-amber-50" },
  { key: "campanhas_andamento", label: "Campanhas Ativas", icon: CalendarClock, color: "text-rose-600", bg: "bg-rose-50" },
];

const PIE_COLORS = ["#10B981", "#94A3B8", "#F59E0B"];

export default function Dashboard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data));
  }, []);

  if (!data)
    return <div className="flex justify-center py-20"><div className="h-8 w-8 rounded-full border-4 border-emerald-500 border-t-transparent animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="dashboard-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Visão geral da sua clínica e disparos.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => navigate("/clientes")} className="bg-emerald-500 hover:bg-emerald-600" data-testid="dashboard-new-client">
            <UserPlus className="h-4 w-4 mr-2" /> Novo Cliente
          </Button>
          <Button onClick={() => navigate("/disparos")} variant="outline" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid="dashboard-new-campaign">
            <Send className="h-4 w-4 mr-2" /> Novo Disparo
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {KPIS.map((k) => (
          <div key={k.key} className="group bg-white rounded-2xl border border-emerald-900/5 p-5 shadow-soft hover:shadow-soft-lg hover:-translate-y-0.5 transition-all duration-300" data-testid={`kpi-${k.key}`}>
            <div className={`h-11 w-11 rounded-xl ${k.bg} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300`}>
              <k.icon className={`h-5 w-5 ${k.color}`} />
            </div>
            <p className="text-2xl font-bold text-slate-900 font-display">{data.kpis[k.key]}</p>
            <p className="text-xs text-slate-500 mt-1">{k.label}</p>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-soft lg:col-span-1">
          <h3 className="font-semibold text-slate-800 mb-4">Novos clientes por mês</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.new_per_month}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" fill="#10B981" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-soft lg:col-span-1">
          <h3 className="font-semibold text-slate-800 mb-4">Mensagens (últimos 7 dias)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data.messages_per_day}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="day" tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="count" stroke="#059669" strokeWidth={3} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-soft lg:col-span-1">
          <h3 className="font-semibold text-slate-800 mb-4">Ativos x Inativos</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={data.active_vs_inactive} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                {data.active_vs_inactive.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent + Followups */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-soft lg:col-span-2">
          <h3 className="font-semibold text-slate-800 mb-4">Últimos clientes cadastrados</h3>
          <div className="space-y-2" data-testid="recent-clients">
            {data.recent_clients.map((c) => (
              <div key={c.id} onClick={() => navigate(`/clientes/${c.id}`)} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-slate-50 cursor-pointer">
                <div className="h-9 w-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-sm font-semibold">
                  {c.name?.[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{c.name}</p>
                  <p className="text-xs text-slate-400">{c.whatsapp || "sem WhatsApp"}</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full border ${statusColor(c.status)}`}>{c.status}</span>
                <span className="text-xs text-slate-400 hidden sm:block">{fmtDate(c.created_at)}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-soft">
          <h3 className="font-semibold text-slate-800 mb-4">Follow-ups de hoje</h3>
          <div className="space-y-2" data-testid="followups-today">
            {data.followups_today.length === 0 && <p className="text-sm text-slate-400">Nenhum follow-up para hoje.</p>}
            {data.followups_today.map((f) => (
              <div key={f.id} onClick={() => f.client_id && navigate(`/clientes/${f.client_id}`)} className="flex items-center gap-3 p-2.5 rounded-lg bg-amber-50 border border-amber-100 cursor-pointer">
                <CalendarClock className="h-4 w-4 text-amber-600 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{f.client_name}</p>
                  {f.note && <p className="text-xs text-slate-500 truncate">{f.note}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
